import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import '../styles/Leave.css';

const apiBase = () => `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/leave`;

const NUMBER_FIELDS = [
  ['annual_leave_days', 'Annual leave days'],
  ['paid_sick_days', 'Paid sick days'],
  ['maternity_weeks', 'Maternity weeks'],
  ['maternity_postnatal_weeks', 'Compulsory weeks after birth'],
  ['maternity_extension_weeks', 'Maximum maternity weeks'],
  ['paternity_weeks', 'Paternity weeks'],
  ['min_service_years', 'Years of service before annual leave'],
];

const ALLOWANCE_FIELDS = [
  ['daily_rate_divisor', 'Days in a month for daily pay'],
  ['allowance_entitled_days', 'Days paid for annual leave'],
  ['forfeited_leave_multiplier', 'Times higher if leave is stopped'],
];

const DEFAULT_BRACKETS = [
  { min_years: 1, max_years: 5, days: 55 },
  { min_years: 6, max_years: 10, days: 60 },
  { min_years: 11, max_years: '', days: 65 },
];

const policyForForm = (policy) => {
  const stored = Array.isArray(policy.end_of_service_brackets) ? policy.end_of_service_brackets : [];
  const brackets = (stored.length ? stored : DEFAULT_BRACKETS).map((row) => ({
    min_years: row.min_years ?? '',
    max_years: row.max_years == null || row.max_years === '' ? '' : row.max_years,
    days: row.days ?? '',
  }));
  return {
    ...policy,
    daily_rate_divisor: policy.daily_rate_divisor ?? 22,
    allowance_entitled_days: policy.allowance_entitled_days ?? 28,
    forfeited_leave_multiplier: policy.forfeited_leave_multiplier ?? 1.5,
    end_of_service_brackets: brackets,
  };
};

const LeavePolicyPage = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('/leave-policy', 'Edit');
  const [policy, setPolicy] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [holiday, setHoliday] = useState({ holiday_date: '', name: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const response = await axios.get(`${apiBase()}/policy`);
    setPolicy(policyForForm(response.data.policy));
    setHolidays(response.data.holidays || []);
  };

  useEffect(() => {
    load().catch((err) => {
      console.error(err);
      setError('Could not load the leave policy.');
    });
  }, []);

  const savePolicy = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.put(`${apiBase()}/policy`, policy);
      setPolicy(policyForForm(response.data));
      setMessage('Leave policy saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the leave policy.');
    } finally {
      setBusy(false);
    }
  };

  const addHoliday = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await axios.post(`${apiBase()}/holidays`, holiday);
      setHoliday({ holiday_date: '', name: '' });
      setMessage('Public holiday added.');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add this holiday.');
    } finally {
      setBusy(false);
    }
  };

  const removeHoliday = async (id) => {
    setBusy(true);
    setError('');
    try {
      await axios.delete(`${apiBase()}/holidays/${id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove this holiday.');
    } finally {
      setBusy(false);
    }
  };

  if (!policy) {
    return (
      <div className="leave-page">
        <h2>Leave Policy</h2>
        {error && <p className="leave-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="leave-page">
      <h2>Leave Policy</h2>
      <p>Defaults follow the Employment Act 2023. Day counts the Act does not set can be changed here.</p>
      {error && <p className="leave-error">{error}</p>}
      {message && <p className="leave-ok">{message}</p>}
      <div className="leave-panel">
        <div className="leave-form">
          {NUMBER_FIELDS.map(([name, label]) => (
            <div className="form-group" key={name}>
              <label htmlFor={name}>{label}</label>
              <input
                id={name}
                type="number"
                step="0.01"
                value={policy[name] ?? ''}
                disabled={!canEdit}
                onChange={(event) => setPolicy((current) => ({ ...current, [name]: event.target.value }))}
              />
            </div>
          ))}
          {canEdit && <button type="button" onClick={savePolicy} disabled={busy}>Save policy</button>}
        </div>
      </div>

      <div className="leave-panel">
        <h3>Allowance policy</h3>
        <p className="leave-note">
          Leave pay uses base pay, divided by the days in a month, then multiplied by the leave days.
          If leave is stopped, that amount is multiplied by the extra rate.
          End of service uses the same base pay. Leave the end year blank when a tier continues.
        </p>
        <div className="leave-form">
          {ALLOWANCE_FIELDS.map(([name, label]) => (
            <div className="form-group" key={name}>
              <label htmlFor={name}>{label}</label>
              <input
                id={name}
                type="number"
                step="0.01"
                min="0"
                value={policy[name] ?? ''}
                disabled={!canEdit}
                onChange={(event) => setPolicy((current) => ({ ...current, [name]: event.target.value }))}
              />
            </div>
          ))}
        </div>
        <h3>End of service days</h3>
        <table className="allowance-brackets">
          <thead>
            <tr>
              <th>From year</th>
              <th>To year</th>
              <th>Days</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(policy.end_of_service_brackets || []).map((row, index) => (
              <tr key={index}>
                <td>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    aria-label={`Tier ${index + 1} from year`}
                    value={row.min_years}
                    disabled={!canEdit}
                    onChange={(event) => setPolicy((current) => {
                      const brackets = current.end_of_service_brackets.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, min_years: event.target.value } : item
                      ));
                      return { ...current, end_of_service_brackets: brackets };
                    })}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    aria-label={`Tier ${index + 1} to year`}
                    value={row.max_years}
                    disabled={!canEdit}
                    onChange={(event) => setPolicy((current) => {
                      const brackets = current.end_of_service_brackets.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, max_years: event.target.value } : item
                      ));
                      return { ...current, end_of_service_brackets: brackets };
                    })}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    aria-label={`Tier ${index + 1} days`}
                    value={row.days}
                    disabled={!canEdit}
                    onChange={(event) => setPolicy((current) => {
                      const brackets = current.end_of_service_brackets.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, days: event.target.value } : item
                      ));
                      return { ...current, end_of_service_brackets: brackets };
                    })}
                  />
                </td>
                <td>
                  {canEdit && (
                    <button
                      type="button"
                      className="danger"
                      disabled={busy}
                      onClick={() => setPolicy((current) => ({
                        ...current,
                        end_of_service_brackets: current.end_of_service_brackets.filter((_, itemIndex) => itemIndex !== index),
                      }))}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {canEdit && (
          <div className="leave-actions">
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => setPolicy((current) => ({
                ...current,
                end_of_service_brackets: [
                  ...(current.end_of_service_brackets || []),
                  { min_years: '', max_years: '', days: '' },
                ],
              }))}
            >
              Add tier
            </button>
            <button type="button" onClick={savePolicy} disabled={busy}>Save policy</button>
          </div>
        )}
      </div>

      <div className="leave-panel">
        <h3>Public holidays</h3>
        <p>These dates are paid and do not use annual leave.</p>
        {canEdit && (
          <div className="leave-form">
            <div className="form-group">
              <label htmlFor="holiday-date">Date</label>
              <input
                id="holiday-date"
                type="date"
                value={holiday.holiday_date}
                onChange={(event) => setHoliday((current) => ({ ...current, holiday_date: event.target.value }))}
              />
            </div>
            <div className="form-group">
              <label htmlFor="holiday-name">Name</label>
              <input
                id="holiday-name"
                type="text"
                value={holiday.name}
                onChange={(event) => setHoliday((current) => ({ ...current, name: event.target.value }))}
              />
            </div>
            <button type="button" onClick={addHoliday} disabled={busy}>Add holiday</button>
          </div>
        )}
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {holidays.map((row) => (
              <tr key={row.holiday_id}>
                <td>{String(row.holiday_date).slice(0, 10)}</td>
                <td>{row.name}</td>
                <td>
                  {canEdit && (
                    <button type="button" className="danger" disabled={busy} onClick={() => removeHoliday(row.holiday_id)}>
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {holidays.length === 0 && (
              <tr>
                <td colSpan="3">No public holidays yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeavePolicyPage;
